import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Save, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

export default function CorrectionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [pageUrls, setPageUrls] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recorrecting, setRecorrecting] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [sRes, stRes] = await Promise.all([
        api.get(`/sessions/${id}`),
        api.get(`/sessions/${id}/students`),
      ]);
      setSession(sRes.data);
      setStudents(stRes.data);
    } catch {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const openStudent = async (student) => {
    try {
      const res = await api.get(`/sessions/${id}/students/${student.id}`);
      setSelected(res.data);
      setCorrections(res.data.corrections || []);
      setCurrentPage(0);

      // Load rendered page images (handles both image uploads and PDF pages)
      try {
        const imgRes = await api.get(`/sessions/${id}/students/${student.id}/page-images`);
        setPageUrls(imgRes.data.images || []);
      } catch {
        setPageUrls([]);
      }
    } catch {
      toast.error("Erro ao carregar teste");
    }
  };

  const closeDialog = () => {
    setSelected(null);
    setCorrections([]);
    setPageUrls([]);
    setCurrentPage(0);
  };

  const updateScore = (cId, val) => {
    setCorrections((prev) => prev.map((c) => (c.id === cId ? { ...c, score: parseFloat(val) || 0 } : c)));
  };

  const updateFeedback = (cId, val) => {
    setCorrections((prev) => prev.map((c) => (c.id === cId ? { ...c, ai_feedback: val } : c)));
  };

  const saveCorrections = async () => {
    setSaving(true);
    try {
      for (const c of corrections) {
        await api.put(`/sessions/${id}/corrections/${c.id}`, { score: c.score, feedback: c.ai_feedback });
      }
      toast.success("Correções guardadas");
      await loadData();
      closeDialog();
    } catch {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const recorrect = async () => {
    if (!selected) return;
    setRecorrecting(true);
    try {
      await api.post(`/sessions/${id}/students/${selected.id}/correct`);
      const res = await api.get(`/sessions/${id}/students/${selected.id}`);
      setSelected(res.data);
      setCorrections(res.data.corrections || []);
      toast.success("Teste recorrigido");
      await loadData();
    } catch {
      toast.error("Erro na recorreção");
    } finally {
      setRecorrecting(false);
    }
  };

  const total = corrections.reduce((a, c) => a + (c.score || 0), 0);
  const maxTotal = corrections.reduce((a, c) => a + (c.max_score || 0), 0);
  const correctedCount = students.filter((s) => ["corrected", "reviewed"].includes(s.status)).length;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="correction-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Revisão de Correções</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {session?.title} &middot; {correctedCount}/{students.length} corrigidos
          </p>
        </div>
        <Button onClick={() => navigate(`/tests/${id}/evaluation`)} disabled={correctedCount === 0} className="active:scale-95 transition-transform" data-testid="go-evaluation-btn">
          Mapa de Avaliação <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {students.map((student) => (
          <Card
            key={student.id}
            className="cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            onClick={() => openStudent(student)}
            data-testid={`student-card-${student.id}`}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono font-bold text-lg text-primary">#{student.student_number}</span>
                <Badge
                  className={
                    student.status === "corrected" || student.status === "reviewed"
                      ? "bg-green-100 text-green-800 border-green-200"
                      : "bg-amber-100 text-amber-800 border-amber-200"
                  }
                >
                  {student.status === "reviewed" ? "Revisto" : student.status === "corrected" ? "Corrigido" : "Pendente"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {student.page_count > 1 ? `${student.page_count} páginas` : student.pages?.[0]?.original_filename || student.original_filename}
              </p>
              {student.total_score != null && (
                <p className="font-mono text-2xl font-bold mt-2">{student.total_score} <span className="text-sm font-normal text-muted-foreground">pts</span></p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Correction Dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-3 font-heading">
              <span className="font-mono text-primary">Aluno #{selected?.student_number}</span>
              {selected?.status === "reviewed" && <Badge className="bg-green-100 text-green-800">Revisto</Badge>}
              {pageUrls.length > 1 && (
                <Badge variant="outline" className="font-mono">
                  Página {currentPage + 1}/{pageUrls.length}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 min-h-0 overflow-hidden">
            {/* Left: Document Viewer */}
            <div className="lg:col-span-3 border-r overflow-auto bg-muted/30 p-4 relative">
              {pageUrls.length > 0 && pageUrls[currentPage] ? (
                <img src={pageUrls[currentPage]} alt={`Teste p.${currentPage + 1}`} className="w-full rounded-lg shadow-sm" data-testid="test-image" />
              ) : (
                <div className="h-full min-h-[300px] flex items-center justify-center text-center px-6" data-testid="no-image-placeholder">
                  <p className="text-sm text-muted-foreground">
                    Não foi possível mostrar a imagem do teste.<br />
                    Pode rever e ajustar as classificações no painel ao lado.
                  </p>
                </div>
              )}

              {/* Page Navigation */}
              {pageUrls.length > 1 && (
                <div className="sticky bottom-4 flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex gap-1">
                    {pageUrls.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-8 h-8 rounded-md text-xs font-mono font-bold transition-colors ${
                          i === currentPage ? "bg-primary text-primary-foreground" : "bg-white border hover:bg-muted"
                        }`}
                        data-testid={`page-btn-${i}`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(pageUrls.length - 1, currentPage + 1))}
                    disabled={currentPage >= pageUrls.length - 1}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Right: Grading */}
            <ScrollArea className="lg:col-span-2">
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold">Correção</h3>
                  <span className="font-mono text-lg font-bold">{total.toFixed(1)}<span className="text-sm font-normal text-muted-foreground">/{maxTotal}</span></span>
                </div>
                <Button variant="outline" size="sm" onClick={recorrect} disabled={recorrecting} className="w-full" data-testid="recorrect-btn">
                  <Sparkles className="h-3 w-3 mr-1.5" />
                  {recorrecting ? "A recorrigir..." : "Recorrigir com IA"}
                </Button>
                <Separator />
                {corrections.map((c) => (
                  <div key={c.id} className="space-y-2 p-3 rounded-lg bg-muted/50" data-testid={`correction-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <Label className="font-mono font-bold text-primary">Q{c.question_number}</Label>
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          max={c.max_score}
                          value={c.score}
                          onChange={(e) => updateScore(c.id, e.target.value)}
                          className="w-20 text-center font-mono h-8"
                          data-testid={`score-input-${c.id}`}
                        />
                        <span className="text-xs text-muted-foreground">/{c.max_score}</span>
                      </div>
                    </div>
                    {c.student_answer && (
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        <span className="font-medium">Resp:</span> {c.student_answer}
                      </p>
                    )}
                    <Input
                      value={c.ai_feedback || ""}
                      onChange={(e) => updateFeedback(c.id, e.target.value)}
                      placeholder="Feedback..."
                      className="text-xs h-8"
                      data-testid={`feedback-input-${c.id}`}
                    />
                  </div>
                ))}
                <Button onClick={saveCorrections} disabled={saving} className="w-full active:scale-95 transition-transform" data-testid="save-corrections-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "A guardar..." : "Guardar Alterações"}
                </Button>
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
